import React, { useState, useEffect, useCallback } from 'react';
import SupplierGrid from './SupplierGrid';
import SupplierModal from './SupplierModal';
import DeleteConfirmModal from '../../common/DeleteConfirmModal';
import ViewToolbar from '../../common/ViewToolbar';
import { useModal } from '../../../hooks/useModal';
import { useDebounce } from '../../../hooks/useDebounce';
import * as supplierService from '../../../services/supplierService';

export default function SuppliersView() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm);
  const [gridApi, setGridApi] = useState(null);

  const editModal = useModal();
  const deleteModal = useModal();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setSuppliers(await supplierService.fetchSuppliers(debouncedSearchTerm));
    } catch (err) {
      console.error('Error fetching suppliers:', err);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearchTerm]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async (formData) => {
    try {
      if (editModal.record) {
        await supplierService.updateSupplier(editModal.record.supplier_id, formData);
      } else {
        await supplierService.createSupplier(formData);
      }
      editModal.close();
      fetchData();
    } catch (err) {
      alert('Failed to save: ' + err.message);
    }
  };

  const handleDelete = async () => {
    try {
      await supplierService.deleteSupplier(deleteModal.record.supplier_id);
      deleteModal.close();
      fetchData();
    } catch (err) {
      alert('Failed to delete: ' + err.message);
    }
  };

  return (
    <>
      <ViewToolbar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onAddNew={() => editModal.open(null)}
        onExportCsv={() => gridApi?.exportToCsv()}
        placeholder="Search suppliers..."
      />
      <div className="flex-1 min-h-0">
        {loading ? (
          <div className="flex justify-center mt-10">Loading...</div>
        ) : (
          <SupplierGrid
            rowData={suppliers}
            onEdit={(r) => editModal.open(r)}
            onDelete={(r) => deleteModal.open(r)}
            onGridReady={setGridApi}
          />
        )}
      </div>
      <SupplierModal
        isOpen={editModal.isOpen}
        onClose={editModal.close}
        onSubmit={handleSave}
        initialData={editModal.record}
        title={editModal.record ? 'Edit Supplier' : 'Add New Supplier'}
      />
      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={deleteModal.close}
        onConfirm={handleDelete}
        recordName={deleteModal.record?.supplier_name || 'this record'}
      />
    </>
  );
}
